import { EmailOutlined, Facebook, Instagram, Language, LinkedIn, PersonOutlineOutlined, X } from '@mui/icons-material';
import React, { Fragment, useEffect, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import useAddRequestStore from 'store/addRequestStore';
import { ContactInfo, PropertyLeadRequestKeys } from 'types/PropertyLead';
import AddRequestHeader from './AddRequestHeader';
import Dropdown from './DropDown';
import { useGetPropertyRequests } from 'hooks/usePropertyRequests';

interface ContactInformationProps {
    isEditMode?: boolean;
}

export default function ContactInformation({ isEditMode }: ContactInformationProps) {
    const { requestData, setRequestData, clearErrors, errors } = useAddRequestStore();
    const { data: userRequests } = useGetPropertyRequests({ isArchived: false }, false);
    const hasPrefilledRef = useRef(false);

    useEffect(() => {
        const leads = userRequests?.data?.data?.leads;

        // Only attempt to prefill if we haven't done it before
        if (leads?.length > 0 && !hasPrefilledRef.current) {
            // Check if any of the contact info fields have actual values
            const hasExistingContactInfo =
                requestData.contactInfo && Object.values(requestData.contactInfo).some((value) => value !== undefined && value !== '');

            if (!hasExistingContactInfo) {
                // Sort requests by createdAt to get the most recent one
                const sortedRequests = [...leads].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

                const mostRecentRequest = sortedRequests[0];

                if (mostRecentRequest?.contactInfo?.[0]) {
                    setRequestData({
                        contactInfo: {
                            name: mostRecentRequest.contactInfo[0].name,
                            email: mostRecentRequest.contactInfo[0].email,
                            phone: mostRecentRequest.contactInfo[0].phone,
                            website: mostRecentRequest.contactInfo[0].website,
                            facebook: mostRecentRequest.contactInfo[0].facebook,
                            instagram: mostRecentRequest.contactInfo[0].instagram,
                            linkedin: mostRecentRequest.contactInfo[0].linkedin,
                            twitter: mostRecentRequest.contactInfo[0].twitter,
                            contactTime: mostRecentRequest.contactInfo[0].contactTime,
                        },
                    });
                    hasPrefilledRef.current = true;
                }
            }
        }
    }, [userRequests, setRequestData]);

    return (
        <div className={isEditMode ? '!overflow-hidden' : ''}>
            <AddRequestHeader title="Contact Information" description="Provide your contact details so others can easily reach out to you." />
            <div className="lg:space-y-6 space-y-4 mt-4 lg:mt-6">
                <ItemContainer
                    title="Your Name"
                    description="Enter your full name for agent communication. Your name will not be publicly visible in the request"
                    children={
                        <>
                            <Input
                                placeholder="Your Name"
                                Icon={PersonOutlineOutlined}
                                isEditMode={isEditMode}
                                value={requestData.contactInfo?.name}
                                onChange={(e) => {
                                    clearErrors('contactInfo.name');
                                    setRequestData({ contactInfo: { ...requestData.contactInfo, name: e.target.value } });
                                }}
                            />
                            {errors['contactInfo.name'] && <p className="text-red-500 text-sm !mt-2">{errors['contactInfo.name']}</p>}
                        </>
                    }
                />
                <ItemContainer
                    title="Phone Number"
                    description="Add your phone number to allow calls or text messages. Ensure you include your country code"
                    children={
                        <>
                            <div className={`${isEditMode ? 'w-full' : 'default-lg:w-10/12 default-xl:w-5/12 default-md:w-2/3 w-full'}`}>
                                <PhoneInput
                                    country="es"
                                    localization={{ es: 'Spain', de: 'Germany' }}
                                    inputStyle={{
                                        width: '100%',
                                        height: '43px',
                                    }}
                                    preferredCountries={['es', 'de']}
                                    priority={{
                                        es: 0,
                                        de: 1,
                                    }}
                                    value={requestData.contactInfo?.phone}
                                    onChange={(value) => {
                                        clearErrors('contactInfo.phone');
                                        setRequestData({ contactInfo: { ...requestData.contactInfo, phone: value } });
                                    }}
                                />
                            </div>
                            {errors['contactInfo.phone'] && <p className="text-red-500 text-sm !mt-2">{errors['contactInfo.phone']}</p>}
                        </>
                    }
                />
                <ItemContainer
                    title="Email Address"
                    description="Provide an email address for correspondence. This is ideal for detailed or formal communications"
                    children={
                        <>
                            <Input
                                placeholder="example@gmail.com"
                                Icon={EmailOutlined}
                                isEditMode={isEditMode}
                                value={requestData.contactInfo?.email}
                                onChange={(e) => {
                                    clearErrors('contactInfo.email');
                                    setRequestData({ contactInfo: { ...requestData.contactInfo, email: e.target.value } });
                                }}
                            />
                            {errors['contactInfo.email'] && <p className="text-red-500 text-sm !mt-2">{errors['contactInfo.email']}</p>}
                        </>
                    }
                />
                <ItemContainer
                    title="Social Connect (Optional)"
                    description="Optionally, share your social media profiles to make connecting easier and build trust. Only provide the platforms you're comfortable sharing."
                    children={
                        <div className={`flex flex-wrap gap-4 ${isEditMode ? 'w-full' : 'default-lg:w-10/12 default-xl:w-5/12 default-md:w-2/3 w-full'}`}>
                            {[
                                {
                                    icon: <Language className="text-black/60 w-5 h-5" />,
                                    label: 'Website',
                                    placeholder: 'www.hellohere.es',
                                    key: 'website' as keyof Omit<ContactInfo, 'id' | 'propertyLeadId'>,
                                },
                                {
                                    icon: <Facebook className="text-black/60 w-5 h-5" />,
                                    label: 'Facebook',
                                    placeholder: 'www.facebook.com',
                                    key: 'facebook' as keyof Omit<ContactInfo, 'id' | 'propertyLeadId'>,
                                },
                                {
                                    icon: <Instagram className="text-black/60 w-5 h-5" />,
                                    label: 'Instagram',
                                    placeholder: 'www.instagram.com',
                                    key: 'instagram' as keyof Omit<ContactInfo, 'id' | 'propertyLeadId'>,
                                },
                                {
                                    icon: <LinkedIn className="text-black/60 w-5 h-5" />,
                                    label: 'LinkedIn',
                                    placeholder: 'www.linkedin.com',
                                    key: 'linkedin' as keyof Omit<ContactInfo, 'id' | 'propertyLeadId'>,
                                },
                                {
                                    icon: <X className="text-black/60 w-5 h-5" />,
                                    label: 'X',
                                    placeholder: 'www.x.com',
                                    key: 'twitter' as keyof Omit<ContactInfo, 'id' | 'propertyLeadId'>,
                                },
                            ].map((item, index) => (
                                <Fragment key={index}>
                                    <div className="flex default-sm:items-center default-sm:flex-row flex-col sm:default-sm:gap-2 gap-4 w-full">
                                        <div className="flex items-center gap-2 min-w-[120px]">
                                            {item.icon}
                                            <h5 className="whitespace-nowrap">{item.label}</h5>
                                        </div>
                                        <div className="flex-1">
                                            <Input
                                                placeholder={item.placeholder}
                                                showIcon={false}
                                                isEditMode={isEditMode}
                                                value={requestData.contactInfo?.[item.key]}
                                                onChange={(e) => {
                                                    clearErrors(`contactInfo.${item.key}` as PropertyLeadRequestKeys);
                                                    setRequestData({ contactInfo: { ...requestData.contactInfo, [item.key]: e.target.value } });
                                                }}
                                            />
                                            {errors[`contactInfo.${item.key}` as PropertyLeadRequestKeys] && (
                                                <p className="text-red-500 text-sm hidden default-sm:block !mt-2">
                                                    {errors[`contactInfo.${item.key}` as PropertyLeadRequestKeys]}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {errors[`contactInfo.${item.key}` as PropertyLeadRequestKeys] && (
                                        <p className="text-red-500 text-sm block default-sm:hidden">
                                            {errors[`contactInfo.${item.key}` as PropertyLeadRequestKeys]}
                                        </p>
                                    )}
                                </Fragment>
                            ))}
                        </div>
                    }
                />
                <ItemContainer
                    title="Contact Time(Optional)"
                    description="Let others know the best time to reach out to you. This helps avoid contacting you at inconvenient hours."
                    children={
                        <>
                            <div className={`${isEditMode ? 'w-full' : 'default-lg:w-10/12  default-md:w-2/3 w-full'}`}>
                                <Dropdown
                                    options={['Morning (8am-12pm)', 'Afternoon (12pm-4pm)', 'Evening (4pm-8pm)', 'Night (8pm-12am)']}
                                    isShown={false}
                                    heightClass="h-12"
                                    isEditMode={isEditMode}
                                    value={requestData.contactInfo?.contactTime}
                                    onChange={(value) => {
                                        clearErrors('contactInfo.contactTime');
                                        setRequestData({ contactInfo: { ...requestData.contactInfo, contactTime: value as string } });
                                    }}
                                    showClearButton={false}
                                />
                            </div>
                            {errors['contactInfo.contactTime'] && <p className="text-red-500 text-sm !mt-2">{errors['contactInfo.contactTime']}</p>}
                        </>
                    }
                    showBorder={false}
                />
                <ItemContainer
                    title="Buyer Identification"
                    description="This is a private note for your reference only. It won't be visible to others and helps you identify which buyer this request is for."
                    children={
                        <>
                            <div className={`${isEditMode ? 'w-full' : 'default-lg:w-10/12 default-xl:w-5/12 default-md:w-2/3 w-full'}`}>
                                <Input
                                    placeholder="Enter buyer identification note"
                                    showIcon={false}
                                    isEditMode={isEditMode}
                                    value={requestData.contactInfo?.buyerIdentification}
                                    onChange={(e) => {
                                        clearErrors('contactInfo.buyerIdentification');
                                        setRequestData({ contactInfo: { ...requestData.contactInfo, buyerIdentification: e.target.value } });
                                    }}
                                />
                            </div>
                            {errors['contactInfo.buyerIdentification'] && (
                                <p className="text-red-500 text-sm !mt-2">{errors['contactInfo.buyerIdentification']}</p>
                            )}
                        </>
                    }
                    showBorder={false}
                />
            </div>
        </div>
    );
}

interface Props {
    title: string;
    description: string;
    children?: React.ReactNode;
    showBorder?: boolean;
}

function ItemContainer({ title, description, children, showBorder = true }: Props) {
    return (
        <>
            <div className="space-y-3">
                <label htmlFor="title" className="block text-base  font-semibold text-light-black">
                    {title}
                </label>
                <p className="text-[12px]  text-light-black/80 mb-2">{description}</p>
                {children}
            </div>
            {showBorder && <div className="border border-gray-100  w-full " />}
        </>
    );
}

function Input({
    Icon,
    placeholder,
    showIcon = true,
    className,
    isEditMode,
    value,
    onChange,
}: {
    Icon?: React.ElementType;
    placeholder: string;
    showIcon?: boolean;
    className?: string;
    isEditMode?: boolean;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
    return (
        <div
            className={`relative flex items-center ${isEditMode ? 'w-full' : showIcon && !isEditMode ? 'default-lg:w-10/12 default-xl:w-5/12 default-md:w-2/3 w-full' : 'w-full'} ${className}`}
        >
            {Icon && <Icon className="absolute left-3 text-black/60 w-5 h-5" />}
            <input
                type="text"
                className={`w-full ${showIcon ? 'pl-12' : 'pl-2'} py-3 border rounded-md focus:outline-none`}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
            />
        </div>
    );
}
